import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { inp } from "./invShared";

const FIELDS = [["name", "Vendor name *"], ["rep", "Sales rep"], ["phone", "Phone"], ["email", "Email"], ["account_number", "Account #"], ["delivery_days", "Delivery days"], ["terms", "Payment terms"]];

export function VendorDialog({ open, onClose, initial, onSaved }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "" });
  const [similar, setSimilar] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(initial || { name: "" }); setSimilar(null); } }, [open, initial]);
  const save = async (confirm_duplicate = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/inv/vendors", { ...f, confirm_duplicate });
      qc.invalidateQueries({ queryKey: ["/inv/meta"] }); qc.invalidateQueries({ queryKey: ["/inv/vendors"] });
      toast.success(f.id ? "Vendor updated" : `Vendor “${data.name}” added`);
      onSaved?.(data); onClose();
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.detail?.similar) setSimilar(e.response.data.detail);
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="vendor-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit vendor" : "New vendor"}</DialogTitle><DialogDescription>{f.id ? "Changes apply to all invoices from this vendor." : "Your invoice stays open — nothing you've entered is lost."}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">{FIELDS.map(([k, l]) => <input key={k} className={`${inp} ${k === "name" ? "col-span-2" : ""}`} placeholder={l} value={f[k] || ""} onChange={(e) => { setF({ ...f, [k]: e.target.value }); if (k === "name") setSimilar(null); }} data-testid={`vendor-dialog-${k}`} />)}</div>
        {similar && <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm space-y-2" data-testid="vendor-dup-warning">
          <div className="flex gap-2"><AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />{similar.msg}</div>
          {!f.id && similar.similar.map((s) => <Button key={s.id} variant="outline" className="h-9 w-full justify-start" onClick={() => { onSaved?.({ id: s.id, name: s.name, existing: true }); onClose(); }} data-testid={`vendor-use-existing-${s.id}`}>Use existing: {s.name} ({Math.round(s.score * 100)}% match)</Button>)}
          <Button variant="ghost" className="h-9 w-full" onClick={() => save(true)} disabled={busy} data-testid="vendor-create-anyway">It's a different company — save anyway</Button>
        </div>}
        <Button className="h-11" onClick={() => save(false)} disabled={busy || !f.name?.trim()} data-testid="vendor-dialog-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{f.id ? "Save changes" : "Add vendor"}</Button>
      </DialogContent>
    </Dialog>
  );
}
